import { useState, useRef } from 'react';

const AppStoreButton = ({ className = '' }: { className?: string }) => (
  <a
    href="https://apps.apple.com/us/app/tread-ai/id6755405651"
    target="_blank"
    rel="noopener noreferrer"
    className={`inline-flex items-center gap-2.5 px-5 py-3 bg-black text-white rounded-xl text-sm font-semibold hover:bg-gray-800 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 ${className}`}
  >
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
    </svg>
    Download on App Store
  </a>
);

const STEPS = [
  {
    num: '01',
    title: 'Identify Your Vehicle',
    desc: 'Enter your VIN or license plate number. We instantly look up your vehicle details via the NHTSA database -- year, make, model, and tire specifications.',
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z" />
      </svg>
    ),
    color: 'from-blue-500 to-blue-600',
    shadowColor: 'shadow-blue-500/30',
  },
  {
    num: '02',
    title: 'Snap Tire Photos',
    desc: 'Take a clear photo of each tire tread. Our guide overlay shows you the ideal angle and distance for the most accurate reading.',
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
      </svg>
    ),
    color: 'from-violet-500 to-purple-600',
    shadowColor: 'shadow-violet-500/30',
  },
  {
    num: '03',
    title: 'AI Analyzes Instantly',
    desc: 'Our YOLOv8 + TFLite ML pipeline measures tread depth to +/-1.4mm, detects uneven wear patterns, and reads the DOT code to calculate tire age.',
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
      </svg>
    ),
    color: 'from-emerald-500 to-teal-600',
    shadowColor: 'shadow-emerald-500/30',
  },
  {
    num: '04',
    title: 'Get Your Report',
    desc: 'Receive a detailed safety report with per-tire ratings (OK, Watch, Replace Soon, Unsafe), actionable recommendations, and a shareable PDF export.',
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
      </svg>
    ),
    color: 'from-amber-500 to-orange-600',
    shadowColor: 'shadow-amber-500/30',
  },
];

const TECH_SPECS = [
  { label: 'Tread Depth Accuracy', value: '+/- 1.4mm' },
  { label: 'Analysis Time', value: '< 5 seconds' },
  { label: 'Object Detection', value: 'YOLOv8' },
  { label: 'Depth Estimation', value: 'TFLite CNN' },
  { label: 'DOT Code Reading', value: 'OCR Pipeline' },
  { label: 'Vehicle Lookup', value: 'NHTSA API' },
];

export const HowItWorks = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const slides = [
    { image: '/content-1.png', title: 'Enter Vehicle Info' },
    { image: '/content-2.png', title: 'Capture Tire Photo' },
    { image: '/content-3.png', title: 'Get AI Results' },
  ];

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };
  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0 && currentSlide < slides.length - 1) setCurrentSlide(currentSlide + 1);
      else if (diff < 0 && currentSlide > 0) setCurrentSlide(currentSlide - 1);
    }
  };

  return (
    <div className="space-y-16 md:space-y-24">

      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-brand-900 rounded-3xl" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-500/20 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-purple-500/15 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4" />

        <div className="relative px-6 sm:px-10 py-14 sm:py-20 md:py-24">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-6">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              <span className="text-emerald-300 text-sm font-medium">ML-Powered</span>
              <span className="text-white/30">|</span>
              <span className="text-white/70 text-sm">4 Simple Steps</span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white mb-4 tracking-tight leading-[1.1]">
              From Photo to
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-brand-400 via-blue-400 to-purple-400">
                Safety Report
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed">
              TreadAI uses computer vision and machine learning to analyze your tire tread depth, detect wear patterns, and generate a professional safety report -- all from a phone photo.
            </p>

            <AppStoreButton className="px-8 py-4 text-lg shadow-xl shadow-black/30" />
          </div>
        </div>
      </section>

      {/* ===== APP SCREENSHOTS ===== */}
      <section>
        <div className="text-center mb-10">
          <span className="inline-block text-sm font-bold text-brand-600 tracking-widest uppercase mb-3">See It In Action</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-3">The App Experience</h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">Clean, guided flow from vehicle lookup to detailed results</p>
        </div>

        {/* Mobile: Swipeable Carousel */}
        <div className="sm:hidden">
          <div
            className="relative overflow-hidden"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="flex transition-transform duration-300 ease-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {slides.map((slide, i) => (
                <div key={i} className="w-full flex-shrink-0 px-8">
                  <div className="relative w-56 mx-auto">
                    <div className="absolute inset-0 bg-gradient-to-br from-brand-400 to-purple-500 rounded-[2rem] blur-2xl opacity-25" />
                    <div className="relative rounded-[1.5rem] overflow-hidden shadow-2xl border-[3px] border-gray-800 bg-gray-900">
                      <div className="bg-gray-900 h-5 flex items-center justify-center">
                        <div className="w-14 h-1.5 bg-gray-700 rounded-full" />
                      </div>
                      <img src={slide.image} alt={slide.title} className="w-full h-auto" />
                    </div>
                  </div>
                  <p className="text-center mt-4 text-sm font-semibold text-gray-700">{slide.title}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-center gap-2 mt-5">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentSlide(i)}
                className={`h-2 rounded-full transition-all ${
                  currentSlide === i ? 'bg-brand-500 w-6' : 'bg-gray-300 w-2'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Desktop: Side by side with arrows */}
        <div className="hidden sm:flex items-end justify-center gap-4 md:gap-6">
          {slides.map((slide, i) => (
            <div key={i} className="flex items-end gap-4 md:gap-6">
              <div className="flex flex-col items-center group">
                <div className={`relative ${i === 1 ? 'w-36 md:w-44 lg:w-52' : 'w-28 md:w-36 lg:w-44'}`}>
                  <div className={`absolute inset-0 rounded-2xl blur-xl opacity-20 ${i === 1 ? 'bg-brand-500' : 'bg-gray-400'}`} />
                  <div className={`relative rounded-2xl overflow-hidden shadow-2xl border-2 ${i === 1 ? 'border-brand-300 ring-2 ring-brand-500/30' : 'border-white/50'} group-hover:scale-105 transition-transform duration-300`}>
                    <img src={slide.image} alt={slide.title} className="w-full h-auto" />
                  </div>
                </div>
                <span className={`mt-3 text-sm font-semibold ${i === 1 ? 'text-brand-600' : 'text-gray-600'}`}>
                  {i + 1}. {slide.title}
                </span>
              </div>
              {i < slides.length - 1 && (
                <svg className="w-6 h-6 text-brand-400 flex-shrink-0 mb-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ===== 4-STEP PROCESS ===== */}
      <section>
        <div className="text-center mb-12">
          <span className="inline-block text-sm font-bold text-emerald-600 tracking-widest uppercase mb-3">Process</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-3">How It Works</h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">Four steps from photo to professional tire safety report</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {STEPS.map((step) => (
            <div key={step.num} className="group">
              <div className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 h-full flex gap-5">
                {/* Left: number + icon */}
                <div className="flex-shrink-0">
                  <div className={`w-14 h-14 bg-gradient-to-br ${step.color} rounded-2xl flex items-center justify-center text-white shadow-lg ${step.shadowColor} group-hover:scale-110 transition-transform`}>
                    {step.icon}
                  </div>
                  <div className="text-xs font-extrabold text-gray-300 tracking-widest mt-3 text-center">{step.num}</div>
                </div>
                {/* Right: content */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== TECH UNDER THE HOOD ===== */}
      <section className="relative">
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-gray-900 rounded-3xl p-8 sm:p-12 overflow-hidden">
          <div className="absolute top-0 right-0 w-80 h-80 bg-brand-500/10 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 left-0 w-60 h-60 bg-purple-500/10 rounded-full blur-[80px]" />

          <div className="relative">
            <div className="text-center mb-10">
              <span className="inline-block text-sm font-bold text-brand-400 tracking-widest uppercase mb-3">Technology</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-3">Under the Hood</h2>
              <p className="text-slate-400 text-lg max-w-lg mx-auto">Purpose-built ML models trained on thousands of tire images</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
              {TECH_SPECS.map((spec) => (
                <div key={spec.label} className="bg-white/5 backdrop-blur-sm rounded-2xl p-5 border border-white/10 text-center hover:bg-white/10 transition-colors">
                  <div className="text-xl sm:text-2xl font-extrabold text-white mb-1">{spec.value}</div>
                  <div className="text-sm text-slate-400 font-medium">{spec.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA BANNER ===== */}
      <section className="group relative">
        <div className="absolute inset-0 bg-gradient-to-r from-brand-500 to-purple-600 rounded-3xl blur-xl opacity-40 group-hover:opacity-60 transition-opacity" />
        <div className="relative bg-gradient-to-r from-brand-600 via-brand-700 to-purple-700 rounded-3xl p-8 sm:p-12 text-center overflow-hidden">
          <div className="relative">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-3">
              Ready to Check Your Tires?
            </h2>
            <p className="text-white/80 text-lg mb-6 max-w-md mx-auto">
              Download TreadAI free on the App Store
            </p>
            <AppStoreButton className="justify-center px-8 py-4 text-lg" />
          </div>
        </div>
      </section>
    </div>
  );
};
